import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Loader, RefreshCw, AlertTriangle } from 'lucide-react';
import { getAccessToken } from '../services/auth';

// ARE THE OUTREACH EMAILS LANDING?
//
// Rachael, 2026-09-16: replies to the delivered-by-day invites fell hard and she asked whether
// they were going to Junk. This card is the answer as far as our side can give one, and it is
// shared by the invite tab and the win-back tab so the two can never read the numbers differently.
//
// ⚠️ THE REP SPLIT COMES FIRST BECAUSE IT IS WHAT FOUND THE CAUSE. Every provider halved together
// that week, which is not what junk filtering looks like; one rep went to zero replies on one date
// while the other two held. A per-provider table alone would have pointed at the wrong thing.
//
// ⚠️ OPENS ARE A TREND, NEVER A RATE TO QUOTE. Apple pre-fetches every image, Outlook blocks them,
// Gmail caches. A provider whose opens fall while the others hold is the junk signal.

async function fetchDeliverability(kind, weeks) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base + '/outreach-deliverability', {
    method: 'POST', headers, body: JSON.stringify({ kind, weeks }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'deliverability ' + res.status);
  return res.json();
}

// A week is Monday to Sunday (Postgres date_trunc('week')), shown as the whole range. A lone
// "Sep 14" read as either the start or the end; Greg could not tell which.
const fmtWeek = (w) => {
  const a = new Date(w + 'T12:00:00');
  const b = new Date(a);
  b.setDate(a.getDate() + 6);
  const mon = (d) => d.toLocaleDateString('en-US', { month: 'short' });
  return mon(a) + ' ' + a.getDate() + ' to ' + (a.getMonth() === b.getMonth() ? '' : mon(b) + ' ') + b.getDate();
};
const firstName = (s) => String(s || '').split(' ')[0];

// Colour a reply rate only against the rep's own history, never a fixed target: 3% is a bad week
// for somebody who normally gets 12% and a fine one for a list that has always run at 4%.
function rateColor(rate, typical) {
  if (rate == null) return 'var(--gray-400)';
  if (typical && typical >= 4 && rate < typical / 3) return '#b42318';
  return 'var(--navy)';
}

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 12.5, verticalAlign: 'top', whiteSpace: 'nowrap' };

export default function OutreachDeliverability({ kind = 'all', title }) {
  // Open unless this person has closed it. Greg, 2026-09-16: "I want the stats on my screen" -
  // collapsed by default, the numbers were one click away and read as missing.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('deliverabilityOpen:' + kind) !== '0'; } catch (_) { return true; }
  });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await fetchDeliverability(kind, 7)); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [kind]);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem('deliverabilityOpen:' + kind, next ? '1' : '0'); } catch (_) { /* private window */ }
  };

  const weeks = data ? [...new Set(data.reps.flatMap((r) => r.weeks.map((w) => w.week)))].sort() : [];
  const tracked = data && data.tracking.trackedSends > 0;
  const sc = data && data.scanner;
  const scannerBad = sc && (sc.runsLast26h === 0 || sc.diedLast26h > 0 || (sc.lastFailures || 0) > 0);

  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, background: '#fff', margin: '0 0 16px' }}>
      <button type="button" onClick={toggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        {open ? <ChevronDown size={15} color="var(--navy)" /> : <ChevronRight size={15} color="var(--navy)" />}
        <b style={{ fontSize: 13, color: 'var(--navy)' }}>{title || 'Are these emails landing?'}</b>
        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Replies and opens by rep and by email provider</span>
      </button>

      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          {err && <div style={{ fontSize: 12.5, color: '#a12020', marginBottom: 8 }}>{err}</div>}
          {!data ? (
            <div style={{ fontSize: 12.5, color: 'var(--gray-500)' }}><Loader size={12} className="spin" /> Loading...</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--gray-600)', marginBottom: 10 }}>
                <span style={{ color: scannerBad ? '#b42318' : 'var(--gray-600)' }}>
                  {scannerBad && <AlertTriangle size={11} style={{ verticalAlign: -1, marginRight: 3 }} />}
                  Reply check: {sc.lastFinished ? 'last finished ' + sc.lastFinished : 'has not finished a run yet'}
                  {sc.runsLast26h === 0 ? ' - no run in the last day, so reply counts may be stale' : ''}
                  {sc.diedLast26h > 0 ? ' - ' + sc.diedLast26h + ' run(s) did not finish' : ''}
                  {(sc.lastFailures || 0) > 0 ? ' - last run hit ' + sc.lastFailures + ' mailbox errors' : ''}
                </span>
                <span>
                  Open tracking: {tracked
                    ? data.tracking.trackedSends + ' tracked sends' + (data.tracking.lastHit ? ', last open ' + data.tracking.lastHit : ', no opens yet')
                    : 'not live yet - it switches on by itself once vip.crabplace.com is set up'}
                </span>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={load} disabled={busy}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', fontSize: 11.5, borderRadius: 6, border: '1px solid var(--gray-300)', background: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <RefreshCw size={11} /> Refresh
                </button>
              </div>

              {/* By rep, by week. Replies first - they are the one number that is certain. */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={th}>Week (Mon to Sun)</th>
                      {weeks.map((w) => <th key={w} style={{ ...th, textAlign: 'right' }}>{fmtWeek(w)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((r) => {
                      const cells = Object.fromEntries(r.weeks.map((w) => [w.week, w]));
                      const tot = r.weeks.reduce((a, w) => ({ s: a.s + w.sent, rp: a.rp + w.replied }), { s: 0, rp: 0 });
                      const typical = tot.s ? (100 * tot.rp) / tot.s : null;
                      return (
                        <tr key={r.rep} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                          <td style={{ ...td, fontWeight: 700, color: 'var(--navy)' }}>{firstName(r.rep)}</td>
                          {weeks.map((w) => {
                            const c = cells[w];
                            if (!c) return <td key={w} style={{ ...td, textAlign: 'right', color: 'var(--gray-300)' }}>-</td>;
                            return (
                              <td key={w} style={{ ...td, textAlign: 'right' }}>
                                <div>
                                  <b style={{ color: rateColor(c.replyRate, typical) }}>{c.replyRate}%</b>
                                  <span style={{ color: 'var(--gray-400)' }}> {c.replied}/{c.sent}</span>
                                </div>
                                {c.tracked > 0 && (
                                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                                    opened {c.openRate}%
                                    {/* A rep's own Outlook loads the pixel when the draft opens. None
                                        at all on a week of sends means her drafts are not opening. */}
                                    <span style={{ color: c.draftOpened === 0 ? '#b42318' : 'var(--gray-400)' }}>
                                      {' '}· drafts {c.draftOpened}/{c.tracked}
                                    </span>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', margin: '6px 0 14px' }}>
                Reply rate, replied of sent. The latest week is still collecting replies, so it always reads low.
                Red means well under that rep's own usual rate.
              </div>

              {/* By provider. A junk problem usually hits one provider while the others hold. */}
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={th}>Provider</th>
                    <th style={{ ...th, textAlign: 'right' }}>Replies, 2 weeks before</th>
                    <th style={{ ...th, textAlign: 'right' }}>Replies, last 2 weeks</th>
                    <th style={{ ...th, textAlign: 'right' }}>Opened, last 2 weeks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.providers.map((p) => (
                    <tr key={p.provider} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ ...td, fontWeight: 600, color: 'var(--navy)' }}>{p.provider}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {p.prior ? <>{p.prior.replyRate}% <span style={{ color: 'var(--gray-400)' }}>of {p.prior.sent}</span></> : '-'}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {p.recent ? <>{p.recent.replyRate}% <span style={{ color: 'var(--gray-400)' }}>of {p.recent.sent}</span></> : '-'}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {p.recent && p.recent.tracked > 0
                          ? <>{p.recent.openRate}% <span style={{ color: 'var(--gray-400)' }}>of {p.recent.tracked}</span></>
                          : <span style={{ color: 'var(--gray-400)' }}>not tracked yet</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
